import React from 'react';
import { Button, Modal, ModalProps } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { Form, Formik } from 'formik';
import { object, string } from 'yup';
import { useSelector } from 'react-redux';
import { map } from 'lodash';

import { ServerError } from '@components/Helper';
import { FormGroupSelect, FormGroupControl } from '@components/Base';
import { ActionRequestStatuses, tActionRequestStatus, ActionRequest } from '@models/ActionRequest';

import { ActionRequestUpdatePayload } from './action';
import { selectFeatureActionRequest } from './selector';
import { mergeInitialValues } from '@utils/formik';

export type ActionRequestFormValues = Pick<ActionRequestUpdatePayload, 'status' | 'title' | 'body'>;

const GROUP_PROPS = { className: 'mb-3' };

type FormFormProps = Pick<ModalProps, 'onHide'> & {
  isEdit?: boolean;
};

const FormForm = ({ onHide, isEdit = false }: FormFormProps) => {
  const { t } = useTranslation();
  const { serverErrors } = useSelector(selectFeatureActionRequest);

  return (
    <Form>
      <Modal.Header closeButton>
        <Modal.Title>Add Request</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {isEdit && (
          <FormGroupSelect
            asRow
            name="status"
            label={t('common:status')}
            required
            groupProps={GROUP_PROPS}
          >
            {map(ActionRequestStatuses, (value) => (
              <option key={value} value={value}>
                {tActionRequestStatus(value)}
              </option>
            ))}
          </FormGroupSelect>
        )}

        <FormGroupControl
          asRow
          name="title"
          label={t('common:title')}
          required
          groupProps={GROUP_PROPS}
        />

        <FormGroupControl
          asRow
          name="body"
          as="textarea"
          label={t('common:body')}
          rows={5}
          groupProps={GROUP_PROPS}
        />
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          {t('confirmModal.cancel')}
        </Button>
        <Button type="submit" variant="primary">
          {t('common:save')}
        </Button>
      </Modal.Footer>
      <ServerError errors={serverErrors} />
    </Form>
  );
}

const defaultValues: ActionRequestFormValues = {
  status: '',
  title: '',
  body: '',
};

type Props = ModalProps & {
  onSubmit: (values: ActionRequestFormValues) => void;
  actionRequest: ActionRequest | null;
}

const ActionRequestFormModal = ({ onSubmit, actionRequest, ...props }: Props) => {
  const { t } = useTranslation();
  const isEdit = !!actionRequest;
  const initialValues = mergeInitialValues(defaultValues, actionRequest);

  const validationSchema = object({
    status: isEdit ? string().required(t('validation:required')) : string().optional(),
    title: string().required(t('validation:required')),
    body: string().optional(),
  });

  return (
    <Modal {...props} size="xl">
      <Formik
        initialValues={initialValues}
        onSubmit={onSubmit}
        validationSchema={validationSchema}
      >
        <FormForm onHide={props.onHide} isEdit={isEdit} />
      </Formik>
    </Modal>
  )
};

export { ActionRequestFormModal };
